import React, { forwardRef, Ref, useCallback, useRef } from "react";

import { StyledDiv, StyledInput, StyledSpan } from "./Styles";
import { useForkRef } from "../../utils";
import { Variant } from "../Typography/Typography";

export interface EditableContentInterface {
  /** Typography variant. */
  variant?: Variant;
  /** Typography weight. */
  weight?: "light" | "regular" | "bold" | "medium";
  /** Input props. */
  inputProps?: Partial<React.ComponentPropsWithoutRef<"input">>;
  /** Disable edit mode. */
  disabled?: boolean;
  /** Wrapper reference. */
  wrapRef?: Ref<HTMLDivElement>;
}

export const EditableContent = forwardRef<
  HTMLDivElement,
  EditableContentInterface &
    Omit<React.ComponentPropsWithoutRef<"div">, keyof EditableContentInterface>
>(
  (
    {
      className,
      variant = "Body 2",
      weight,
      disabled = false,
      inputProps,
      wrapRef,
      children,
      ...props
    },
    ref
  ) => {
    const intInputRef = useRef<HTMLInputElement>(null);
    const handleRef = useForkRef(ref, intInputRef);

    const handleBlur = useCallback(
      (evt) => {
        if (intInputRef?.current) {
          intInputRef.current.scrollLeft = 0;
        }
        typeof inputProps?.onBlur === `function` && inputProps?.onBlur(evt);
      },
      [inputProps]
    );

    return (
      <StyledDiv className={className} ref={wrapRef} {...props}>
        <StyledSpan
          variant={variant}
          forwardedAs="span"
          weight={weight}
          $disabled={disabled}
          className="presentation"
        >
          {children ? children : inputProps?.value}
        </StyledSpan>
        <StyledInput
          {...inputProps}
          ref={handleRef}
          forwardedAs="input"
          variant={variant}
          weight={weight}
          value={inputProps?.value}
          onBlur={handleBlur}
          disabled={disabled}
          data-form-type="other"
        />
      </StyledDiv>
    );
  }
);

EditableContent.displayName = "EditableContent";
